import React, { useEffect, useState } from 'react';
import liveStreamService, { LiveStream, CreateLiveStreamInput } from '../../services/liveStreams';
import { logger } from '../../utils/logger';
import BrowserBroadcaster from '../../components/BrowserBroadcaster';
import './LiveStreams.css';

const AdminLiveStreams: React.FC = () => {
  const [streams, setStreams] = useState<LiveStream[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedStream, setSelectedStream] = useState<LiveStream | null>(null);
  const [showCredentials, setShowCredentials] = useState(false);

  const [formData, setFormData] = useState<CreateLiveStreamInput>({
    title: '',
    description: '',
    streamingMode: 'browser', // Default to browser mode
    category: '',
    tags: [],
    isRecordingEnabled: false,
    isPublic: true
  });

  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    loadStreams();
  }, []);

  // Auto-restore broadcaster modal for active live streams
  useEffect(() => {
    if (streams.length === 0) return;

    // Check localStorage for persisted broadcast state
    const persistedStreamId = localStorage.getItem('activeBroadcastStreamId');

    // Find live stream - either from localStorage or any live stream
    const liveStream = persistedStreamId
      ? streams.find(s => s.streamId === persistedStreamId && s.status === 'live')
      : streams.find(s => s.status === 'live' && s.streamingMode === 'browser');

    if (liveStream && !showCredentials) {
      // Auto-reopen the broadcaster modal for active stream
      handleShowCredentials(liveStream);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streams]);

  // Auto-refresh streams when there are streams in transitional states
  // BUT don't refresh if there's an active browser broadcaster modal open
  // to prevent unmounting the broadcaster component
  useEffect(() => {
    const hasTransitionalStreams = streams.some(
      stream => stream.status === 'starting' ||
                stream.status === 'stopping' ||
                stream.status === 'provisioning'
    );

    // Don't auto-refresh if browser broadcaster modal is open
    if (hasTransitionalStreams && !showCredentials) {
      const interval = setInterval(() => {
        loadStreams();
      }, 5000); // Check every 5 seconds

      return () => clearInterval(interval);
    }
  }, [streams, showCredentials]);

  const loadStreams = async () => {
    try {
      setLoading(true);
      const data = await liveStreamService.getAllLiveStreams();
      setStreams(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load live streams');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: (prev.tags || []).filter(t => t !== tag)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    try {
      setCreating(true);
      await liveStreamService.createLiveStream(formData);

      // Reset form and reload streams
      setFormData({
        title: '',
        description: '',
        streamingMode: 'browser',
        category: '',
        tags: [],
        isRecordingEnabled: false,
        isPublic: true
      });
      setShowCreateForm(false);
      await loadStreams();

      alert('Live stream created successfully! You can now view the stream credentials to start broadcasting.');
    } catch (err: any) {
      logger.error('Error creating live stream:', err);
      setError(err.message || 'Failed to create live stream');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (streamId: string) => {
    if (!window.confirm('Are you sure you want to delete this live stream? This action cannot be undone.')) {
      return;
    }

    try {
      await liveStreamService.deleteLiveStream(streamId);
      await loadStreams();
      alert('Live stream deleted successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to delete live stream');
    }
  };

  const handleStart = async (streamId: string) => {
    try {
      setError(null);
      await liveStreamService.startLiveStream(streamId);
      await loadStreams();
      alert('Live stream starting... This may take a few minutes.');
    } catch (err: any) {
      setError(err.message || 'Failed to start live stream');
    }
  };

  const handleStop = async (streamId: string) => {
    if (!window.confirm('Are you sure you want to stop this live stream?')) {
      return;
    }

    try {
      setError(null);
      setSuccessMessage(null);
      await liveStreamService.stopLiveStream(streamId);

      // Close credentials modal if open
      setShowCredentials(false);
      setSelectedStream(null);

      // Clear persisted broadcast state
      localStorage.removeItem('activeBroadcastStreamId');

      // Show success message
      setSuccessMessage('Live stream stopped successfully');

      // Reload streams immediately
      await loadStreams();

      // Reload again after a short delay to ensure backend has updated
      setTimeout(() => {
        loadStreams();
      }, 1000);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to stop live stream');
    }
  };

  const handleShowCredentials = async (stream: LiveStream) => {
    try {
      const credentials = await liveStreamService.getStreamCredentials(stream.streamId);
      setSelectedStream({ ...stream, ...credentials });
      setShowCredentials(true);

      // Persist to localStorage if this is a browser broadcast
      if (stream.streamingMode === 'browser' && stream.status === 'live') {
        localStorage.setItem('activeBroadcastStreamId', stream.streamId);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load stream credentials');
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'live':
        return 'status-live';
      case 'ready':
        return 'status-ready';
      case 'starting':
      case 'provisioning':
        return 'status-provisioning';
      case 'stopped':
      case 'stopping':
        return 'status-stopped';
      case 'error':
        return 'status-error';
      default:
        return 'status-default';
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-live-streams container">
      <div className="admin-header">
        <div>
          <h1>Manage Live Streams</h1>
          <p className="subtitle">Create and manage your live radio broadcasts</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Cancel' : 'Create New Live Stream'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="error-close">&times;</button>
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          {successMessage}
          <button onClick={() => setSuccessMessage(null)} className="success-close">&times;</button>
        </div>
      )}

      {showCreateForm && (
        <div className="create-stream-form">
          <h2>Create New Live Stream</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="title">Stream Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="e.g., Friday Night Jazz Session"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                rows={3}
                placeholder="Describe your live stream..."
              />
            </div>

            <div className="form-group">
              <label>Streaming Mode</label>
              <div className="streaming-mode-selector">
                <label className="radio-option">
                  <input
                    type="radio"
                    name="streamingMode"
                    value="browser"
                    checked={formData.streamingMode === 'browser'}
                    onChange={handleInputChange}
                  />
                  <div className="radio-content">
                    <span className="radio-title">🌐 Browser Streaming</span>
                    <span className="radio-description">
                      Stream directly from your browser using your microphone or audio interface
                    </span>
                  </div>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="streamingMode"
                    value="rtmp"
                    checked={formData.streamingMode === 'rtmp'}
                    onChange={handleInputChange}
                  />
                  <div className="radio-content">
                    <span className="radio-title">📡 RTMP Streaming</span>
                    <span className="radio-description">
                      Use broadcasting software like OBS Studio (advanced)
                    </span>
                  </div>
                </label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="category">Category</label>
                <input
                  type="text"
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  placeholder="e.g., Music, Talk, News"
                />
              </div>

              <div className="form-group">
                <label>Tags</label>
                <div className="tag-input-wrapper">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    placeholder="Add tags..."
                  />
                  <button type="button" onClick={handleAddTag} className="btn btn-sm">
                    Add
                  </button>
                </div>
                <div className="tags-list">
                  {formData.tags?.map((tag, index) => (
                    <span key={index} className="tag">
                      {tag}
                      <button type="button" onClick={() => handleRemoveTag(tag)} className="tag-remove">
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="isRecordingEnabled"
                    checked={formData.isRecordingEnabled}
                    onChange={handleInputChange}
                  />
                  <span>Enable Recording</span>
                </label>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="isPublic"
                    checked={formData.isPublic}
                    onChange={handleInputChange}
                  />
                  <span>Public Stream</span>
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowCreateForm(false)}
                disabled={creating}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={creating}
              >
                {creating ? 'Creating...' : 'Create Live Stream'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="streams-list">
        <h2>Your Live Streams ({streams.length})</h2>

        {streams.length === 0 ? (
          <p className="no-streams">
            No live streams created yet. Create your first live stream to start broadcasting!
          </p>
        ) : (
          <div className="streams-table">
            {streams.map((stream) => (
              <div key={stream.streamId} className="stream-item">
                <div className="stream-info">
                  <div className="stream-header-row">
                    <h3>{stream.title}</h3>
                    <span className={`badge ${getStatusBadgeClass(stream.status)}`}>
                      {stream.status === 'live' && <span className="pulse-dot"></span>}
                      {stream.status.toUpperCase()}
                    </span>
                  </div>
                  <p className="stream-description">{stream.description}</p>
                  <div className="stream-meta">
                    {stream.category && <span className="badge">{stream.category}</span>}
                    {stream.tags?.map((tag, index) => (
                      <span key={index} className="badge badge-tag">{tag}</span>
                    ))}
                    {stream.status === 'live' && (
                      <span className="badge badge-viewers">
                        👁 {stream.viewerCount} viewers
                      </span>
                    )}
                  </div>
                  {stream.statusMessage && (
                    <p className="status-message">{stream.statusMessage}</p>
                  )}
                </div>
                <div className="stream-actions">
                  <button
                    className="btn btn-sm btn-info"
                    onClick={() => handleShowCredentials(stream)}
                  >
                    Stream Key
                  </button>
                  {(stream.status === 'ready' || stream.status === 'stopped') && (
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => handleStart(stream.streamId)}
                    >
                      Start
                    </button>
                  )}
                  {(stream.status === 'live' || stream.status === 'starting' || stream.status === 'ready') && (
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={() => handleStop(stream.streamId)}
                    >
                      Stop
                    </button>
                  )}
                  {stream.status !== 'live' && stream.status !== 'starting' && (
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(stream.streamId)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Stream Credentials Modal */}
      {showCredentials && selectedStream && (
        <div className="modal-overlay" onClick={() => setShowCredentials(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {selectedStream.streamingMode === 'browser' ? 'Browser Broadcaster' : 'Stream Credentials'}
              </h2>
              <button
                className="modal-close"
                onClick={() => setShowCredentials(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              {selectedStream.streamingMode === 'browser' ? (
                <BrowserBroadcaster
                  streamId={selectedStream.streamId}
                  streamName={selectedStream.title}
                  onStatusChange={(status) => {
                    if (status === 'live') {
                      // Persist active broadcast to localStorage
                      localStorage.setItem('activeBroadcastStreamId', selectedStream.streamId);
                      // Note: Don't call loadStreams() here as it causes the component to unmount
                      // The stream status poller inside BrowserBroadcaster handles status updates
                    }
                  }}
                />
              ) : (
                <>
                  <p className="credentials-intro">
                    Use these credentials to configure your broadcasting software (e.g., OBS Studio):
                  </p>

                  <div className="credential-item">
                    <label>RTMP URL (Primary)</label>
                    <div className="credential-value">
                      <code>{selectedStream.rtmpUrl}</code>
                      <button
                        className="btn btn-sm"
                        onClick={() => navigator.clipboard.writeText(selectedStream.rtmpUrl || '')}
                      >
                        Copy
                      </button>
                    </div>
                  </div>

                  {selectedStream.rtmpBackupUrl && (
                    <div className="credential-item">
                      <label>RTMP URL (Backup)</label>
                      <div className="credential-value">
                        <code>{selectedStream.rtmpBackupUrl}</code>
                        <button
                          className="btn btn-sm"
                          onClick={() => navigator.clipboard.writeText(selectedStream.rtmpBackupUrl || '')}
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="credential-item">
                    <label>Stream Key</label>
                    <div className="credential-value">
                      <code>{selectedStream.streamKey}</code>
                      <button
                        className="btn btn-sm"
                        onClick={() => navigator.clipboard.writeText(selectedStream.streamKey || '')}
                      >
                        Copy
                      </button>
                    </div>
                  </div>

                  <div className="credentials-warning">
                    <strong>⚠️ Keep your stream key private!</strong> Anyone with this key can broadcast to your stream.
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-primary"
                onClick={() => {
                  // Don't close modal if browser broadcast is active
                  if (selectedStream.streamingMode !== 'browser' || selectedStream.status !== 'live') {
                    setShowCredentials(false);
                  }
                }}
                disabled={selectedStream.streamingMode === 'browser' && selectedStream.status === 'live'}
                title={selectedStream.streamingMode === 'browser' && selectedStream.status === 'live'
                  ? 'Stop broadcast before closing'
                  : undefined}
              >
                {selectedStream.streamingMode === 'browser' && selectedStream.status === 'live'
                  ? 'Broadcasting (Stop to close)'
                  : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLiveStreams;
